import { ListItemIcon, ListItemText, Stack } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { ROUTE_PATH } from "@/common/constants/routing";
import { TenantRequestHelper } from "@/common/helpers/entity/tenantRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { AppPermission, TagEntityType, TenantRequestDto } from "@/core/api/generated/v1";

import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import GeneralAttachedTagsOfEntityEditModal from "../General/GeneralTag/GeneralAttachedTagsOfEntityEditModal";
import TenantRequestDeleteModal from "./TenantRequestDeleteModal";

enum ActionFlags {
  Edit = "Edit",
  EditTags = "EditTags",
  Delete = "Delete",
}

const defaultDisplayProps = {
  edit: true,
  editTags: true,
  delete: true,
};

export type TenantRequestMenuDisplayProps = Partial<typeof defaultDisplayProps>;

interface TriggersProps {
  entity?: TenantRequestDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity: TenantRequestDto;
  disabled?: boolean;
  displayProps?: TenantRequestMenuDisplayProps;
  customActions?: (props: TriggersProps) => ReactNode;
  onUpdate: ((newValue: TenantRequestDto) => void) | undefined;
  onDelete: (() => void) | undefined;
}

export default function TenantRequestMenu({
  entity,
  disabled,
  customActions,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();

  if (!TenantRequestHelper.isSentByMe(currentTenant, entity)) {
    return null;
  }

  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}

          {displayProps?.edit && TenantRequestHelper.isSentByMe(currentTenant, entity) && (
            <AuthorizedMenuItem
              permissions={[AppPermission.TenantRequestManage]}
              disabled={!TenantRequestHelper.canBeEdited(entity)}
              component={RouterLink}
              to={ROUTE_PATH.TENANT_REQUEST_EDIT(entity.id)}
            >
              <ListItemIcon>
                <AppIcon of='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </AuthorizedMenuItem>
          )}
          {displayProps?.editTags && TenantRequestHelper.isSentByMe(currentTenant, entity) && (
            <AuthorizedMenuItem
              permissions={[AppPermission.FleetAppAccess]}
              dense
              onClick={() => {
                startAction(ActionFlags.EditTags);
              }}
            >
              <ListItemIcon>
                <AppIcon of='tag' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit tags</ListItemText>
            </AuthorizedMenuItem>
          )}
          {displayProps?.delete && TenantRequestHelper.isSentByMe(currentTenant, entity) && (
            <AuthorizedMenuItem
              permissions={[AppPermission.TenantRequestManage]}
              disabled={!TenantRequestHelper.canBeDeleted(entity)}
              onClick={() => {
                startAction(ActionFlags.Delete);
              }}
            >
              <ListItemIcon>
                <AppIcon of='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </AuthorizedMenuItem>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Delete */}
          {entity && (
            <TenantRequestDeleteModal
              entity={entity}
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              onDelete={() => {
                onDelete && onDelete();
                completeAction();
              }}
            />
          )}

          {/* Edit tags */}
          {entity && (
            <GeneralAttachedTagsOfEntityEditModal
              open={currentAction === ActionFlags.EditTags}
              onClose={() => cancelAction()}
              entityType={TagEntityType.TenantRequest}
              entityId={entity.id!}
              onSaved={(newValue) => {
                onUpdate &&
                  onUpdate({
                    ...entity,
                    tags: newValue || undefined,
                  });
              }}
            />
          )}
        </Stack>
      )}
    />
  );
}
